import is from '@sindresorhus/is';

import type { UrlTileCodec, UrlTilePayload } from './types';

// Decode/encode pair for one token value. Segment codecs must always encode to a token;
// option codecs may encode to undefined to skip the option entirely.
export type UrlTileValueCodec<TValue, TEncoded extends string | undefined = string | undefined> = {
    readonly decode: (token: string) => unknown;
    readonly encode: (value: TValue) => TEncoded;
};

export type UrlTileSegmentValueCodec<TValue> = UrlTileValueCodec<TValue, string>;
export type UrlTileOptionValueCodec<TValue> = UrlTileValueCodec<TValue>;

type PayloadField<TPayload> = Exclude<keyof TPayload, 'type'> & string;

// A plain field name binds the token verbatim; non-string fields need an explicit value codec.
export type UrlTileSegmentInput<TPayload extends UrlTilePayload> = {
    [K in PayloadField<TPayload>]:
        | (NonNullable<TPayload[K]> extends string ? K : never)
        | {
              readonly codec: UrlTileSegmentValueCodec<NonNullable<TPayload[K]>>;
              readonly field: K;
              readonly optional?: boolean;
          };
}[PayloadField<TPayload>];

export type UrlTileOptionInput<TPayload extends UrlTilePayload> = {
    [K in PayloadField<TPayload>]: {
        readonly codec: UrlTileOptionValueCodec<NonNullable<TPayload[K]>>;
        readonly field: K;
        readonly key: string;
    };
}[PayloadField<TPayload>];

export type UrlTileCodecInput<TPayload extends UrlTilePayload> = {
    // Named options serialized after the segments as `key.value` pairs, in the given order.
    readonly options?: readonly UrlTileOptionInput<TPayload>[];
    // Positional segments serialized directly after the tile type, in the given order.
    readonly segments: readonly UrlTileSegmentInput<TPayload>[];
    readonly type: TPayload['type'];
};

// Empty tokens decode as absent so a dangling option key drops the field, not the tile.
export const urlTileStringCodec: UrlTileSegmentValueCodec<string> = {
    decode: (token) => (is.nonEmptyString(token) ? token : undefined),
    encode: (value) => value,
};

export const urlTileIntegerCodec: UrlTileSegmentValueCodec<number> = {
    decode: (token) => {
        if (!/^\d+$/.test(token)) {
            return undefined;
        }

        const value = Number.parseInt(token, 10);

        return Number.isSafeInteger(value) ? value : undefined;
    },
    encode: (value) => `${value}`,
};

// Encodes a numeric version as a `v`-prefixed token (3 <-> "v3").
export const urlTileVersionNumberCodec: UrlTileSegmentValueCodec<number> = {
    decode: (token) => (token.startsWith('v') ? urlTileIntegerCodec.decode(token.slice(1)) : undefined),
    encode: (value) => `v${value}`,
};

// Encodes a version identifier as a `v`-prefixed token while keeping it a string ("proposal" <-> "vproposal").
export const urlTileVersionStringCodec: UrlTileSegmentValueCodec<string> = {
    decode: (token) => (token.length > 1 && token.startsWith('v') ? token.slice(1) : undefined),
    encode: (value) => `v${value}`,
};

// Maps URL keywords to payload values ({ closed: false, open: true }); unmapped values are not serialized.
export const urlTileKeywordCodec = <TValue>(
    keywords: Readonly<Record<string, TValue>>,
): UrlTileOptionValueCodec<TValue> => ({
    decode: (token) => (Object.hasOwn(keywords, token) ? keywords[token] : undefined),
    encode: (value) => Object.keys(keywords).find((keyword) => keywords[keyword] === value),
});

// The tile schema validates every payload before encode is called, so each field is
// guaranteed to hold its codec's value type and the codecs can be handled type-erased.
const eraseValueCodec = <TEncoded extends string | undefined>(
    codec: UrlTileValueCodec<never, TEncoded>,
): UrlTileValueCodec<unknown, TEncoded> => ({
    decode: codec.decode,
    encode: codec.encode as UrlTileValueCodec<unknown, TEncoded>['encode'],
});

export const createUrlTileCodec = <TPayload extends UrlTilePayload>({
    options = [],
    segments,
    type,
}: UrlTileCodecInput<TPayload>): UrlTileCodec<TPayload> => {
    const typeTokenCount = type.split('.').length;
    const normalizedSegments = segments.map((segment) =>
        is.string(segment)
            ? { codec: eraseValueCodec(urlTileStringCodec), field: segment, optional: false }
            : {
                  codec: eraseValueCodec(segment.codec),
                  field: segment.field,
                  optional: segment.optional === true,
              },
    );
    const normalizedOptions = options.map((option) => ({
        codec: eraseValueCodec<string | undefined>(option.codec),
        field: option.field,
        key: option.key,
    }));
    const optionsByKey = new Map(normalizedOptions.map((option) => [option.key, option]));

    const parseTokens = (tokens: readonly string[]): unknown => {
        const payload: Record<string, unknown> = { type: tokens.slice(0, typeTokenCount).join('.') };
        let offset = typeTokenCount;

        for (const segment of normalizedSegments) {
            const token = tokens[offset];

            if (is.undefined(token)) {
                break;
            }

            const value = segment.codec.decode(token);

            if (is.undefined(value)) {
                if (segment.optional) {
                    break;
                }

                offset += 1;
                continue;
            }

            payload[segment.field] = value;
            offset += 1;
        }

        // The tail must be well-formed `key.value` pairs; a misaligned tail drops options by design.
        for (let index = offset; index + 1 < tokens.length; index += 2) {
            const key = tokens[index];
            const token = tokens[index + 1];

            if (is.undefined(key) || is.undefined(token)) {
                continue;
            }

            const option = optionsByKey.get(key);

            if (is.undefined(option)) {
                continue;
            }

            const value = option.codec.decode(token);

            if (!is.undefined(value)) {
                payload[option.field] = value;
            }
        }

        return payload;
    };

    const serialize = (payload: TPayload): string => {
        const tokens = [
            type,
            ...normalizedSegments.flatMap((segment) => {
                const value = payload[segment.field];

                if (segment.optional && is.nullOrUndefined(value)) {
                    return [];
                }

                return [segment.codec.encode(value)];
            }),
        ];

        for (const option of normalizedOptions) {
            const value = payload[option.field];

            if (is.nullOrUndefined(value)) {
                continue;
            }

            const encoded = option.codec.encode(value);

            if (is.nonEmptyString(encoded)) {
                tokens.push(option.key, encoded);
            }
        }

        return tokens.join('.');
    };

    return { parseTokens, serialize, type };
};
